import { validationTranslationKeys } from '@va/constants';
import { useAddNotification } from '@va/dashboard/util-hooks';
import { useTranslate } from '@va/localization';
import { Button, ModalHeader, TextInput } from '@va/ui/design-system';
import { ModalWrapper } from '@va/util/components';
import { useFormik } from 'formik';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { useUpdateConversationMetadata } from '../../api';
import { useAiChatbotContext } from '../aiChatbotContext';

const FORM_ID = 'edit-chat-name-form';

export const EditChatNameModal = () => {
  const translate = useTranslate();
  const { showErrorNotification, showSuccessNotification } = useAddNotification();
  const { isChatNameModalOpen, closeEditChatNameModal, selectedChat, onEditSuccess } = useAiChatbotContext();
  const { execute: updateConversationMetadata, isLoading } = useUpdateConversationMetadata();

  const { handleSubmit, handleChange, values, resetForm, errors, setFieldValue } = useFormik({
    initialValues: { title: selectedChat?.title || '' },
    onSubmit: async (values) => {
      if (!selectedChat?.id) {
        showErrorNotification();
        return;
      }

      try {
        await updateConversationMetadata(selectedChat.id, { title: values.title });
        onEditSuccess && (await onEditSuccess());
        closeEditChatNameModal();
        showSuccessNotification();
      } catch (error) {
        showErrorNotification();
      }
    },
    validationSchema: Yup.object({
      title: Yup.string().trim().required(translate(validationTranslationKeys.required)),
    }),
  });

  useEffect(() => {
    resetForm({ values: { title: selectedChat?.title || '' } });
  }, [selectedChat, resetForm]);

  return (
    <ModalWrapper isModalOpen={isChatNameModalOpen} closeModal={closeEditChatNameModal}>
      <div className='max-w-120'>
        <ModalHeader title={translate('aiChatbot.editChatNameModal.title')} onClose={closeEditChatNameModal} />
        <div className='p-8u pb-0'>
          <form onSubmit={handleSubmit} id={FORM_ID}>
            <TextInput
              error={errors.title}
              value={values.title}
              label={translate('aiChatbot.editChatNameModal.input.label')}
              name='title'
              clearField={() => {
                setFieldValue('title', '');
              }}
              onChange={handleChange}
              autoFocus
            />
          </form>
        </div>
        <div className='flex items-center gap-3 mt-3 p-8u'>
          <Button
            type='button'
            onClick={closeEditChatNameModal}
            className='flex-1'
            color='tertiary'
            text={translate('button.cancel')}
          />
          <Button
            type='submit'
            form={FORM_ID}
            disabled={!!errors.title || isLoading}
            className='flex-1'
            color='primary'
            text={translate('button.submit')}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};
